'use client';

import { cn } from '@/app/_lib/utils';
import {
  NotificationToastType,
  TOAST_TRANSITION_SLEEP,
} from '@/app/context/globalNotificationContext';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';

interface Props extends NotificationToastType {
  handleClose: () => void;
}

const kindClasses = {
  'error': 'text-[#f05252] bg-[#fde8e8]',
  'info': 'text-[#3f83f8] bg-[#e1effe]',
  'success': 'text-[#0e9f6e] bg-[#def7ec]',
};

const renderIcon = (kind: string) => {
  switch (kind) {
    case 'error':
      return (
        <svg
          className="w-5 h-5"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 11.793a1 1 0 1 1-1.414 1.414L10 11.414l-2.293 2.293a1 1 0 0 1-1.414-1.414L8.586 10 6.293 7.707a1 1 0 0 1 1.414-1.414L10 8.586l2.293-2.293a1 1 0 0 1 1.414 1.414L11.414 10l2.293 2.293Z" />
        </svg>
      );
    case 'success':
      return (
        <svg
          className="w-5 h-5"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z" />
        </svg>
      );
    case 'info':
      return (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM11.4079 6.5C10.8556 6.5 10.4079 6.94772 10.4079 7.5C10.4079 8.05228 10.8556 8.5 11.4079 8.5H11.4179C11.9702 8.5 12.4179 8.05228 12.4179 7.5C12.4179 6.94772 11.9702 6.5 11.4179 6.5H11.4079ZM10 10C9.44772 10 9 10.4477 9 11C9 11.5523 9.44772 12 10 12H11V15H10C9.44772 15 9 15.4477 9 16C9 16.5523 9.44772 17 10 17H14C14.5523 17 15 16.5523 15 16C15 15.4477 14.5523 15 14 15H13V11C13 10.4477 12.5523 10 12 10H10Z"
            fill="currentColor"
          />
        </svg>
      );
  }
};

export default function NotificationToast({
  show = false,
  handleClose,
  message,
  kind,
}: Props) {
  return (
    <div className="flex w-full flex-col items-center space-y-2 md:items-start">
      {/* Notification panel, dynamically insert this into the live region when it needs to be displayed */}
      <Transition
        show={show}
        as={Fragment} // fixed flickering problem
        enter={`transform ease-out duration-${TOAST_TRANSITION_SLEEP} transition`}
        enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enterTo="translate-y-0 opacity-100 sm:translate-x-0"
        leave={`transition ease-in duration-${TOAST_TRANSITION_SLEEP}`}
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className="pointer-events-auto flex items-center w-full max-w-sm p-4 mb-4 text-purple-dark bg-grey1 rounded-lg drop-shadow-lg"
          role="alert"
        >
          <div
            className={cn(
              'inline-flex items-center justify-center flex-shrink-0 w-8 h-8 rounded-lg ',
              kindClasses[kind]
            )}
          >
            {renderIcon(kind)}
            <span className="sr-only">{kind} icon</span>
          </div>
          <div className="mx-3 font-normal">{message}</div>
          <button
            type="button"
            className="ms-auto -mx-1.5 -my-1.5 bg-grey1 text-grey6 hover:text-purple-dark rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-grey3 inline-flex items-center justify-center h-8 w-8"
            aria-label="Close"
            onClick={handleClose}
          >
            <span className="sr-only">Close</span>
            <svg
              className="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
          </button>
        </div>
      </Transition>
    </div>
  );
}
